// Shared foundation for the report pages.
//
// Report.js -> ReportRouter picks a layout by questionnaire type and renders
// either ReportV1 or ReportV2. This base owns everything that is not layout:
// loading the report payload, saving edited content, the benchmark controls and
// the AI highlights/suggestions calls. Variants implement renderReport() only.
import { Component } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getLanguage } from "../../utils/utils";
import API from "../../api";
import {
  abFuture,
  abInclusion,
  abInnovation,
  abImpact,
  abSatisfaction,
} from "../../components/report/components/ReportTemplates";

const abCategoryDescription = {
  future: abFuture,
  inclusion: abInclusion,
  innovation: abInnovation,
  impact: abImpact,
  satisfaction: abSatisfaction,
};

const translations = {
  hr: {
    aboveBeyond: "Above&Beyond",
    employerPartner: "Employer Partner",
    report: "Izvješće",
    download: "Preuzmi",
    loading: "Učitavam...",
    overallResult: "Ukupni rezultat",
    overallResultIntro:
      "Stručnjaci za ljudske resurse procjenili su vaše HR procese kroz 7 <em>employee life cycle</em> koraka i uporedili ih s najboljim svjetskim praksama. Proces evaluacije temelji se na unapred definiranim standardima.<br/><br/>Evaluacija vaših praksi upravljanja ljudskim potencijalima pokazala je sljedeći rezultat:",
    benchmarkAnalysis: "<em>Benchmark</em> analiza",
    yourOrganization: "Vaša organizacija",
    employerPartnerAvg: "Employer Partner avg.",
    aboveBeyondAvg: "Above&Beyond avg.",
    highlights: "Snage",
    suggestions: "Sugestije",
    aiHighlightsHeading: "AI snage",
    aiSuggestionsHeading: "AI sugestije",
    getAiHighlights: "Generiraj AI snage",
    getAiSuggestions: "Generiraj AI sugestije",
    aiError: "Došlo je do greške prilikom generiranja AI prijedloga.",
    resultsKeyTopics: "Rezultati na ključnim temama",
    resultsKeyTopicsDescription:
      "Evaluacija je napravljena i za 10 ključnih tema <em>employee life cycle</em> modela u odnosu na najbolje svjetske prakse. <br/><br/>Evaluacija je pokazala sljedeći rezultat",
    resultsELCSteps: "Rezultati na <em>employee life cycle</em> koracima",
    resultsDiveIn: "Dublji pogled u rezultate",
    resultsDiveInDescription:
      "Nakon što ste proučili sve glavne rezultate koje ste postigli, donosimo vam prijedloge za poboljšanje praksi upravljanja ljudskim resursima prilagođene upravo vama i detaljan <em>benchmark</em> koji će vas usmjeriti u pravom smjeru.",
    whatsNext: "Što je sljedeće?",
    educationTitle: "Grow to lead, lead to grow.",
    educationIntro:
      "Prema vašim potrebama predlažemo sljedeće razvojne aktivnosti:",
    yourCareTitle: "Vaša briga za zaposlenike u brojevima",
    yourCareTitleMultiline:
      "Vaša briga za zaposlenike<br/><strong>u brojevima</strong>",
    keepInMindTitle: "Razmislite o <strong>povećanju</strong> ovih KPI-eva",
    outroTitle: "Vrijeme je da<br/>podijelite<br/>svoj uspjeh!",
    outroAuditTitle:
      "Zajedno<br/> gradimo odlična<br/> radna mjesta<br/> diljem svijeta",
    changesError: "Došlo je do greškre prilikom spremanja izmjena",
    benchmarkTitle: "Benchmark",
    benchmarkSubmit: "Spremi",
    countryHR: "Hrvatska",
    countryRS: "Srbija",
    country: "Država",
    countries: {
      al: "Albanija",
      at: "Austrija",
      ba: "Bosna i Hercegovina",
      hr: "Hrvatska",
      hu: "Mađarska",
      me: "Crna Gora",
      mk: "Makedonija",
      pl: "Poljska",
      rs: "Srbija",
      si: "Slovenija",
    },
    industry: "Industrija",
    primaryBusinessType: {
      1: "FMCG",
      2: "Financijska",
      3: "ICT",
      4: "Farmaceutska",
      5: "Turizam i ugostiteljstvo",
      6: "Maloprodaja",
      7: "Prerađivačka",
      8: "Poljoprivredna",
      9: "Građevinska",
      10: "Logistika",
      11: "Stručne djelatnosti",
      12: "Mediji",
      13: "Igre na sreću",
      14: "Javni sektor",
      15: "Energetska",
      16: "Ostalo",
    },
    yourResults: "Vaši rezultati",
    yourResultsIntro:
      "U nastavku donosimo vaš ukupni rezultat kao i glavne rezultate koje ste postigli u odnosu na prosjek rezultata koje ostvaruju druge organizacije.",
    neededActivitiesInvestment:
      "Potrebne aktivnosti: kontinuirano ulaganje u zaposlenike",
    neededActivitiesHr: "Potrebne aktivnosti: HR procesi",
    neededActivitiesStrategy:
      "Potrebne aktivnosti: strategija i strateški kapaciteti",
    strengthsStrategy: "Vaše snage: strategija i strateški kapaciteti",
    strengthsInvestment: "Vaše snage: kontinuirano ulaganje u zaposlenike",
    strengthsProcesses: "Vaše snage: HR procesi",
    actionPlanPriorities: "Prioriteti akcijskog plana",
    actionPlanTimeline: "<em>Timeline</em> za provođenje akcijskog plana",
    statusQuoRisks: "Rizici statusa quo",
    companyGroup: "Grupacija",
    doNotShow: "Sakrij",
    show: "Prikaži",
    reportLocked: "Ovaj izvještaj je zaključan za uređivanje.",
    abCoverIntro: "Čestitamo",
    abCoverOutro: "na stjecanju Above and Beyond certifikata!",
    abCoverInfo:
      "Pokazali ste izvrsnost u upravljanju ljudskim potencijalima u Employer Partner certificiranju, a sada ste dosegli i <strong>najvišu razinu kvalitete HR-a.</strong> Ovom certifikacijom potvrdili ste svoje mjesto među <strong>10% najboljih Poslodavaca Partnera.</strong>",
    abCoverCertificated: "Godina certifikacije:",
    abResultsTitle:
      'Pregled <span style={{ color: "#dfaf56" }}>rezultata</span>',
    abResultsInfo:
      "<p>U Employer Partner certifikaciji postigli ste natprosječan ukupni rezultat zbog čega ste ušli u Above and Beyond certifikaciju.</p><p>Povrh toga, dosegli ste Above and Beyond standard koji vas je postavio na 10% najboljih Poslodavca Partnera.</p>",
    abResultsCategoriesTitle:
      'Rezultati u <span style={{ color: "#dfaf56" }}>kategorijama</span>',
    abResultsCategoriesInfo:
      "Ovdje možete vidjeti usporedbu svojih rezultata u svim Above and Beyond kategorijama.",
    abWhatYouDoBest: "Što radite najbolje?",
    abHighlightedNumbers: `<span style="color:#dfaf56">Istaknuti brojevi </span> iz Above and Beyond rezultata`,
    abWordsFromCertificationTeam: `Par riječi<br/> <span style="color:#dfaf56">certifikacijskog tima</span>`,
    sponsorsLabel: "SPONZORI",
    partnersLabel: "PARTNERI",
  },
  en: {
    aboveBeyond: "Above&Beyond",
    employerPartner: "Employer Partner",
    report: "Report",
    download: "Download",
    loading: "Loading...",
    overallResult: "Overall result",
    overallResultIntro:
      "The 7 steps of the Employee Life Cycle are evaluated by the HR Experts from the Certification Team and compared to the best global practices. The process of evaluation is based on strong principles to have one standard for all.<br/><br/>The overall evaluation has shown the following result:",
    benchmarkAnalysis: "Benchmark Analysis",
    yourOrganization: "Your organization",
    employerPartnerAvg: "Employer Partner avg.",
    aboveBeyondAvg: "Above&Beyond avg.",
    highlights: "Highlights",
    suggestions: "Suggestions",
    aiHighlightsHeading: "AI highlights",
    aiSuggestionsHeading: "AI suggestions",
    getAiHighlights: "Get AI highlights",
    getAiSuggestions: "Get AI suggestions",
    aiError: "There was an error generating AI suggestions.",
    resultsKeyTopics: "Results on the key topics",
    resultsKeyTopicsDescription:
      "The 10 key topics of the Employee Life Cycle are evaluated and compared to the best global practices.<br/><br/>The overall evaluation has shown the following result:",
    resultsELCSteps: "Results on the Employee Life Cycle steps",
    resultsDiveIn: "Let's dive in!",
    resultsDiveInDescription:
      "After reading all the main results that you achieved, we bring you tailor-made suggestions for improving HR practices and in-depth benchmark that will navigate you in the right direction.",
    whatsNext: "What's next",
    educationTitle: "Grow to lead, lead to grow.",
    educationIntro:
      "According to your needs, we suggest the following development activities:",
    yourCareTitle: "Your care for employees in numbers",
    yourCareTitleMultiline:
      "Your care for employees<br/><strong>in numbers</strong>",
    keepInMindTitle: "Keep in mind to <strong>increase</strong> these KPIs",
    outroTitle: "It is time to<br/> share your<br/> success!",
    outroAuditTitle:
      "Together<br/> we build great<br/> work places<br/> around the world",
    changesError: "There was an error saving changes.",
    benchmarkTitle: "Benchmark",
    benchmarkSubmit: "Save",
    countryHR: "Croatia",
    countryRS: "Serbia",
    country: "Country",
    countries: {
      al: "Albania",
      at: "Austria",
      ba: "Bosnia and Herzegovina",
      hr: "Croatia",
      hu: "Hungary",
      me: "Montenegro",
      mk: "Macedonia",
      pl: "Poland",
      rs: "Serbia",
      si: "Slovenia",
    },
    industry: "Industry",
    primaryBusinessType: {
      1: "FMCG",
      2: "Financial",
      3: "ICT",
      4: "Pharmaceutical",
      5: "Tourism and Hospitality",
      6: "Retail",
      7: "Manufacturing",
      8: "Agricultural",
      9: "Construction",
      10: "Logistics",
      11: "Professional Services",
      12: "Media",
      13: "Gambling",
      14: "Public Sector",
      15: "Energy",
      16: "Other",
    },
    yourResults: "Your results",
    yourResultsIntro:
      "We present your overall result as well as the main results you have achieved in relation to the average results achieved by other organizations.",
    neededActivitiesInvestment:
      "Required activities: continuous investment in employees",
    neededActivitiesHr: "Required activities: HR processes",
    neededActivitiesStrategy:
      "Required activities: strategy and strategic capacities",
    strengthsStrategy: "Your strengths: strategy and strategic capacities",
    strengthsInvestment: "Your strengths: continuous investment in employees",
    strengthsProcesses: "Your strengths: HR processes",
    actionPlanPriorities: "Action plan priorities",
    actionPlanTimeline: "Timeline for action plan implementation",
    statusQuoRisks: "Risks of the status quo",
    companyGroup: "Group",
    doNotShow: "Do not show",
    show: "Show",
    reportLocked: "This report is locked for editing.",
    abCoverIntro: "Congratulations",
    abCoverOutro: "for reaching Above and Beyond!",
    abCoverInfo:
      "This company showed excellence in human resources management in Employer Partner certification, and now it achieved <strong>the highest level of HR quality.</strong> With this certification, it confirmed its place in the <strong>top 10% of Employer Partners.</strong>",
    abCoverCertificated: "Certificated in:",
    abResultsTitle: 'Results <span style={{ color: "#dfaf56" }}>summary</span>',
    abResultsInfo:
      "<p>In the Employer Partner certification, you reached above average total score which is why you entered the Above and Beyond certification.</p><p>On top of that, you reached the Above and Beyond standard which placed you in the top 10% of Employer Partners.</p>",
    abResultsCategoriesTitle:
      'Results in <span style={{ color: "#dfaf56" }}>categories</span>',
    abResultsCategoriesInfo:
      "Here you can see a comparison of your results in all of the Above and Beyond categories.",
    abWhatYouDoBest: "What do you do best?",
    abHighlightedNumbers: `<span style="color:#dfaf56">Highlighted numbers</span> from Above and Beyond results`,
    abWordsFromCertificationTeam: `A few words from the<br/> <span style="color:#dfaf56">certification team</span>`,
    sponsorsLabel: "SPONSORS",
    partnersLabel: "PARTNERS",
  },
  sr: {
    aboveBeyond: "Above&Beyond",
    employerPartner: "Employer Partner",
    report: "Izveštaj",
    download: "Preuzmi",
    loading: "Učitavam...",
    overallResult: "Ukupni rezultat",
    overallResultIntro:
      "Stručnjaci za ljudske resurse procenili su vaše HR procese kroz 7 <em>employee life cycle</em> koraka i uporedili ih s najboljim svetskim praksama. Proces evaluacije temelji se na unapred definisanim standardima.<br/><br/>Evaluacija vaših praksi upravljanja ljudskim potencijalima pokazala je sledeći rezultat:",
    benchmarkAnalysis: "<em>Benchmark</em> analiza",
    yourOrganization: "Vaša organizacija",
    employerPartnerAvg: "Employer Partner prosek",
    aboveBeyondAvg: "Above&Beyond avg.",
    highlights: "Snage",
    suggestions: "Sugestije",
    aiHighlightsHeading: "AI snage",
    aiSuggestionsHeading: "AI sugestije",
    getAiHighlights: "Generiši AI snage",
    getAiSuggestions: "Generiši AI sugestije",
    aiError: "Došlo je do greške prilikom generisanja AI predloga.",
    resultsKeyTopics: "Rezultati na ključnim temama",
    resultsKeyTopicsDescription:
      "Evaluacija je napravljena i za 10 ključnih tema <em>employee life cycle</em> modela u odnosu na najbolje svetske prakse. <br/><br/>Evaluacija je pokazala sledeći rezultat:",
    resultsELCSteps: "Rezultati na <em>employee life cycle</em> koracima",
    resultsDiveIn: "Dublji pogled u rezultate",
    resultsDiveInDescription:
      "Nakon što ste proučili sve glavne rezultate koje ste postigli, donosimo vam predloge za poboljšanje praksi upravljanja ljudskim resursima prilagođene upravo vama i detaljan <em>benchmark</em> koji će vas uputiti u pravom smeru.",
    whatsNext: "Što je sledeće?",
    educationTitle: "Grow to lead, lead to grow.",
    educationIntro:
      "Prema vašim potrebama predlažemo sledeće razvojne aktivnosti:",
    yourCareTitle: "Vaša briga za zaposlene u brojevima",
    yourCareTitleMultiline:
      "Vaša briga za zaposlene<br/><strong>u brojevima</strong>",
    keepInMindTitle: "Razmislite o <strong>povećanju</strong> ovih KPI-ja",
    outroTitle: "Vreme je da<br/>podelite svoj<br/>uspeh!",
    outroAuditTitle:
      "Zajedno<br/> gradimo odlična<br/> radna mjesta<br/> diljem svijeta",
    changesError: "Došlo je do greškre prilikom spremanja izmjena",
    benchmarkTitle: "Benchmark",
    benchmarkSubmit: "Spremi",
    countryHR: "Hrvatska",
    countryRS: "Srbija",
    country: "Država",
    countries: {
      al: "Albanija",
      at: "Austrija",
      ba: "Bosna i Hercegovina",
      hr: "Hrvatska",
      hu: "Mađarska",
      me: "Crna Gora",
      mk: "Makedonija",
      pl: "Poljska",
      rs: "Srbija",
      si: "Slovenija",
    },
    industry: "Industrija",
    primaryBusinessType: {
      1: "FMCG",
      2: "Financijska",
      3: "ICT",
      4: "Farmaceutska",
      5: "Turizam i ugostiteljstvo",
      6: "Maloprodaja",
      7: "Prerađivačka",
      8: "Poljoprivredna",
      9: "Građevinska",
      10: "Logistika",
      11: "Stručne djelatnosti",
      12: "Mediji",
      13: "Igre na sreću",
      14: "Javni sektor",
      15: "Energetska",
      16: "Ostalo",
    },
    yourResults: "Vaši rezultati",
    yourResultsIntro:
      "U nastavku donosimo vaš ukupni rezultat kao i glavne rezultate koje ste postigli u odnosu na prosjek rezultata koje ostvaruju druge organizacije.",
    neededActivitiesInvestment:
      "Potrebne aktivnosti: kontinuirano ulaganje u zaposlenike",
    neededActivitiesHr: "Potrebne aktivnosti: HR procesi",
    neededActivitiesStrategy:
      "Potrebne aktivnosti: strategija i strateški kapaciteti",
    strengthsStrategy: "Vaše snage: strategija i strateški kapaciteti",
    strengthsInvestment: "Vaše snage: kontinuirano ulaganje u zaposlenike",
    strengthsProcesses: "Vaše snage: HR procesi",
    actionPlanPriorities: "Prioriteti akcijskog plana",
    actionPlanTimeline: "<em>Timeline</em> za provođenje akcijskog plana",
    statusQuoRisks: "Rizici statusa quo",
    companyGroup: "Grupacija",
    doNotShow: "Sakrij",
    show: "Prikaži",
    reportLocked: "Ovaj izveštaj je zaključan za uređivanje.",
    abCoverIntro: "Congratulations",
    abCoverOutro: "for reaching Above and Beyond!",
    abCoverInfo:
      "This company showed excellence in human resources management in Employer Partner certification, and now it achieved <strong>the highest level of HR quality.</strong> With this certification, it confirmed its place in the <strong>top 10% of Employer Partners.</strong>",
    abCoverCertificated: "Certificated in:",
    abResultsTitle: 'Results <span style={{ color: "#dfaf56" }}>summary</span>',
    abResultsInfo:
      "<p>In the Employer Partner certification, you reached above average total score which is why you entered the Above and Beyond certification.</p><p>On top of that, you reached the Above and Beyond standard which placed you in the top 10% of Employer Partners.</p>",
    abResultsCategoriesTitle:
      'Results in <span style={{ color: "#dfaf56" }}>categories</span>',
    abResultsCategoriesInfo:
      "Here you can see a comparison of your results in all of the Above and Beyond categories.",
    abWhatYouDoBest: "What do you do best?",
    abHighlightedNumbers: `<span style="color:#dfaf56">Highlighted numbers</span> from Above and Beyond results`,
    abWordsFromCertificationTeam: `A few words from the<br/> <span style="color:#dfaf56">certification team</span>`,
    sponsorsLabel: "SPONZORI",
    partnersLabel: "PARTNERI",
  },
};

export { translations, abCategoryDescription };

export default class ReportBase extends Component {
  constructor(props) {
    super(props);

    this.state = {
      id: this.props.match.params.id,
      ab: false,
      data: null,
      categories: [],
      content: null,
      contentUpdated: {},
      saveTimeout: null,
      messages: translations[getLanguage()],
      benchmark: false,
      benchmarkLoading: false,
      benchmarkCountry: null,
      benchmarkIndustry: null,
      benchmarkGroup: null,
      benchmarkNew: this.props.match.params.new ? 1 : 0,
      editable: false,
      aiItems: {},
      aiLoading: {},
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    await API.get("", {
      params: {
        action: "companyCategories",
      },
    })
      .then((response) => {
        this.setState({
          categories: response.data,
        });
      })
      .catch((error) => {});

    await API.get("", {
      params: {
        id: this.state.id,
        action: "report",
        benchmarkCountry: this.state.benchmarkCountry,
        benchmarkIndustry: this.state.benchmarkIndustry,
        benchmarkGroup: this.state.benchmarkGroup,
        benchmarkNew: this.state.benchmarkNew,
        language: getLanguage(),
      },
    })
      .then((response) => {
        this.setState({
          data: response.data,
          editable: !response.data.info.reportLocked,
          content: response.data.content ? response.data.content : {},
        });
      })
      .catch((error) => {});
  };

  _benchmarkOnChange = (event) => {
    const name = event.target.name;
    this.setState({
      [name]: event.target.value,
    });
  };

  _benchmarkOnSubmit = async (event) => {
    event.preventDefault();

    this.setState({
      benchmarkLoading: true,
    });

    await this.loadData();

    this.setState({
      benchmarkLoading: false,
    });
  };

  _contentOnChange = (event) => {
    console.log("UPDATING", event.target.name, event.target.value);
    if (this.state.saveTimeout) {
      console.log("Clearing timeout");
      clearTimeout(this.state.saveTimeout);
    }

    let contentUpdated = this.state.contentUpdated;
    contentUpdated[event.target.name] = event.target.value;

    this.setState({
      contentUpdated: contentUpdated,
      // `content` is what the report renders from, so it is updated straight
      // away rather than waiting for the debounced save to come back - the
      // components that are driven by it (the editable card lists and
      // timelines) would otherwise not react until the request resolved.
      content: {
        ...this.state.content,
        [event.target.name]: event.target.value,
      },
      saveTimeout: setTimeout(this._saveContent, 3000),
    });
  };

  _saveContent = async () => {
    console.log("Saving content");

    const contentToSave = this.state.contentUpdated;
    this.setState({
      contentUpdated: {},
    });

    await API.post("", {
      id: this.state.id,
      action: "reportContentSaveItems",
      language: getLanguage(),
      content: contentToSave,
    })
      .then((response) => {
        // Nothing to merge back: _contentOnChange already applied these values
        // to `content`. Re-applying them here would overwrite any edit made
        // while this request was in flight with the older, saved value.
      })
      .catch((error) => {
        toast(this.state.messages.changesError, {
          type: "error",
        });

        this.setState({
          contentUpdated: { ...contentToSave, ...this.state.contentUpdated },
        });
      });
  };

  _getAiItems = async (categoryId, type) => {
    const key = `${type}_${categoryId}`;

    this.setState({
      aiLoading: { ...this.state.aiLoading, [key]: true },
    });

    await API.post("", {
      id: this.state.id,
      action: "reportAiSuggestions",
      language: getLanguage(),
      categoryId: categoryId,
      type: type,
    })
      .then((response) => {
        this.setState({
          aiItems: {
            ...this.state.aiItems,
            [key]: response.data.items || [],
          },
        });
      })
      .catch((error) => {
        toast(this.state.messages.aiError, {
          type: "error",
        });
      })
      .finally(() => {
        this.setState({
          aiLoading: { ...this.state.aiLoading, [key]: false },
        });
      });
  };

  // The variants supply the layout as renderReport(); everything else - state,
  // loading and saving - lives here.
  render() {
    return this.renderReport();
  }
}
